// Chat attachment items (file-stc spec): a message carries a file id plus the
// minimal info to lay it out, never a URL. Receivers resolve ids via getFiles.
// Items stored before the history rewrite (original.path URLs) are still read.
import type { FileInfo, FileKind } from "../api/files";

/** At most this many photos/videos in one chat message (album); stc-chat refuses more. */
export const MAX_ALBUM_ITEMS = 5;

/** One attachment item as published / stored. */
export interface ChatMedia {
  fileId?: string | null;
  kind?: FileKind | string | null;
  mime?: string | null;
  width?: number | null;
  height?: number | null;
  durationMs?: number | null;
  thumbnailFileId?: string | null;
  // Legacy (pre-file-stc) shape, read only.
  name?: string | null;
  original?: LegacyMediaFile | null;
  thumbnail?: LegacyMediaFile | null;
}

interface LegacyMediaFile {
  name?: string | null;
  path?: string | null;
  extra?: { width?: number | null; height?: number | null } | null;
}

/** What a chat message publishes for a confirmed file (nulls left out). */
export function chatMediaOf(info: FileInfo): ChatMedia {
  const m: ChatMedia = { fileId: info.id, kind: info.kind, mime: info.mime };
  if (info.width != null) m.width = info.width;
  if (info.height != null) m.height = info.height;
  if (info.durationMs != null) m.durationMs = info.durationMs;
  if (info.thumbnailFileId) m.thumbnailFileId = info.thumbnailFileId;
  return m;
}

/** IMAGE and VIDEO are media; AUDIO (and anything else) is a file. */
export function isMediaKind(kind: string | null | undefined): boolean {
  return kind === "IMAGE" || kind === "VIDEO";
}

/** A chat video as shown in a bubble (ids only; resolved when rendered). */
export interface ChatVideo {
  fileId: string;
  /** The poster (file-stc thumbnail) id. */
  thumbnailFileId: string | null;
  durationMs: number | null;
  mime: string | null;
  width: number | null;
  height: number | null;
}

export interface MediaFields {
  type: "image" | "video" | "text";
  imageFileId: string | null;
  /** Legacy URL only; new messages resolve imageFileId. */
  imageUrl: string | null;
  imageWidth: number | null;
  imageHeight: number | null;
  video: ChatVideo | null;
  /** Every photo / video item, in album order (legacy URL items included). Empty for text. */
  media: ChatMedia[];
}

/** An item a chat bubble can show: a file-stc photo / video, or a legacy URL photo. */
export function isAlbumItem(item: ChatMedia | null | undefined): item is ChatMedia {
  if (!item) return false;
  if (item.fileId) return isMediaKind(item.kind);
  return !!item.original?.path;
}

/** The message's photo / video items in album order (anything else is left out). */
export function albumItemsOf(media: ChatMedia[] | null | undefined): ChatMedia[] {
  return (media ?? []).filter(isAlbumItem);
}

/** What a chat message publishes for a video the upload queue confirmed READY. */
export function chatVideoMediaOf(v: {
  fileId: string;
  thumbnailFileId: string;
  width: number;
  height: number;
  durationMs: number;
  mime: string;
}): ChatMedia {
  const m: ChatMedia = { fileId: v.fileId, kind: "VIDEO", mime: v.mime };
  if (v.width) m.width = v.width;
  if (v.height) m.height = v.height;
  if (v.durationMs) m.durationMs = v.durationMs;
  m.thumbnailFileId = v.thumbnailFileId;
  return m;
}

/**
 * Bubble fields: every item (`media`, the album) plus the first item's type and fields, which the
 * reply quote and older readers use.
 */
export function mediaFieldsOf(
  media: ChatMedia[] | null | undefined,
  fallbackType: "image" | "video" | "text" = "text",
): MediaFields {
  const items = albumItemsOf(media);
  const item = items[0];
  const none = { imageFileId: null, imageUrl: null, imageWidth: null, imageHeight: null, video: null, media: items };
  if (item?.fileId && item.kind === "VIDEO") {
    return {
      ...none,
      type: "video",
      video: {
        fileId: item.fileId,
        thumbnailFileId: item.thumbnailFileId ?? null,
        durationMs: item.durationMs ?? null,
        mime: item.mime ?? null,
        width: item.width ?? null,
        height: item.height ?? null,
      },
    };
  }
  if (item?.fileId && item.kind === "IMAGE") {
    return {
      ...none,
      type: "image",
      imageFileId: item.fileId,
      imageWidth: item.width ?? null,
      imageHeight: item.height ?? null,
    };
  }
  const legacy = item && !item.fileId ? (item.original?.path ?? null) : null;
  if (legacy) {
    return {
      ...none,
      type: "image",
      imageUrl: legacy,
      imageWidth: item?.original?.extra?.width ?? null,
      imageHeight: item?.original?.extra?.height ?? null,
    };
  }
  return { ...none, type: fallbackType === "video" ? "text" : fallbackType };
}

/** Bytes as the "N.N" of an "N.N MB" label (chat video sizes, upload toast). */
export const toMb = (bytes: number): string => (bytes / 1_000_000).toFixed(1);

/** Photos (legacy URL items count as photos) and videos in a message's album. */
export function albumCounts(media: ChatMedia[] | null | undefined): { photos: number; videos: number } {
  const items = albumItemsOf(media);
  const videos = items.filter((m) => m.kind === "VIDEO").length;
  return { photos: items.length - videos, videos };
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Reply and conversation-list copy for a message (Figma Shared / Chat / Album preview 4343:2578):
 * "3 photos", "2 videos", "Photo + 2 videos", "2 photos + video"; a caption wins on an album
 * ("Album · <caption>"). A single item with a caption shows the caption; no media → the text.
 */
export function albumPreviewText(t: Translate, text: string | null | undefined, media: ChatMedia[] | null | undefined): string {
  const caption = (text ?? "").trim();
  const { photos, videos } = albumCounts(media);
  const total = photos + videos;
  if (total === 0) return caption;
  if (caption) return total > 1 ? t("chat_album_preview_caption", { text: caption }) : caption;
  if (videos === 0) return t("chat_album_preview_photos", { count: photos });
  if (photos === 0) return t("chat_album_preview_videos", { count: videos });
  if (photos === 1) return t("chat_album_preview_photo_videos", { count: videos });
  if (videos === 1) return t("chat_album_preview_photos_video", { n: photos });
  return t("chat_album_preview_photos_videos", { photos, videos });
}

/** A reply quote's line: the quoted text, else its attachment kind ("Photo" / "Video"), as Android. */
export function replyQuoteText(t: Translate, text: string | null | undefined, attachmentType: string | null | undefined): string {
  if (text) return text;
  if (attachmentType === "image") return t("chat_album_preview_photos", { count: 1 });
  if (attachmentType === "video") return t("chat_album_preview_videos", { count: 1 });
  return "";
}
